<template>
  <div id="app">
    <div
      class="slide-type-image-bg grid"
      id="slide-image-bg"
      :class="{'custom-invisible': $store.state.presentation.privateMode}"
    >
      <div v-if="canViewThemeGallery" class="col-start-1 row-start-1 w-full h-full grid">
        <div
          class="col-start-1 row-start-1 w-full h-full"
          v-if="currentPresentationTheme && currentPresentationTheme.baseColour && isSlideTab"
          :style="{'background-color': currentPresentationTheme.baseColour}"
        />
        <div
          class="col-start-1 row-start-1 w-full h-full bg-cover bg-no-repeat bg-center"
          v-if="currentPresentationTheme && currentPresentationTheme.backgroundImage && isSlideTab"
          :style="{
            backgroundImage: `url(${currentPresentationTheme.backgroundImage.large})`,
            opacity: currentPresentationTheme.visibility / 100 }"
        />
        <div
          class="col-start-1 row-start-1 w-full h-full z-0"
          v-if="activeActivityBaseColour && isSlideTab"
          :style="{'background-color': activeActivityBaseColour}"
        />
      </div>
      <div
        class="col-start-1 bg-cover bg-no-repeat bg-center row-start-1 w-full h-full z-0"
        v-if="slide.backgroundImage"
        :style="backgroundStyle"
      />
    </div>
    <!-- not static element -->

    <div class="grid-universal">
      <div class="grid-universal-header">
        <ConnectionInform />
        <SlideProgressBar
          v-if="canShowSlideProgressBar"
          :class="{'custom-invisible': presentation.privateMode}"
        />
        <Tabs />
      </div>
      <router-view class="grid-universal-main" />
      <Footer class="grid-universal-footer" />
    </div>

    <!-- section for non-static elements -->
    <PhotoSwipe />
    <portal-target name="popup" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

// api
import Api from '@/api'

// utils
import Util from '@/util'
import * as zoomUtils from '@/utils/zoom.utils'
import * as Sentry from '@sentry/vue'

// components
import Footer from '@/components/footer/Footer.vue'
import Tabs from '@/components/Tabs.vue'
import SlideProgressBar from '@/components/SlideProgressBar.vue'
import PhotoSwipe from '@/components/PhotoSwipe.vue'
import ConnectionInform from '@/components/connection/ConnectionInform.vue'

// containers
import FooterContainer from '@/containers/footer/Footer'
import cookie from '@/services/cookie'
import Vue from 'vue'
import UserApi from '@/api/user'
import Toasted from 'vue-toasted'
import * as localStorageExpiry from '@/utils/local.storage.expiry'
import { getPresentingLanguageFromCdn } from '@/utils/language'

// mixin
import AttendedPresentationMixin from '@/mixins/attended-presentation.mixin'

Vue.use(Toasted, {
  duration: 3000,
  singleton: true,
})

const baseBgColor = '#ffffff'
const SHOW_TOASTED_LOGIN = 'show-toasted-login'
const TABS = [
  'slide',
  'lookback',
  'feedback',
  'profile',
]

export default {
  components: {
    Footer: FooterContainer(Footer),
    Tabs,
    SlideProgressBar,
    PhotoSwipe,
    ConnectionInform,
  },
  mixins: [AttendedPresentationMixin],
  data() {
    return {
      width: 668,
      iframeHeight: 375.75,
      countRender: 0,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      activeTabView: (state) => state.activeTabView,
      currentUser: (state) => state.currentUser,
      audienceId: (state) => state.audienceId,
      currentTokenValue: (state) => state.currentTokenValue,
    }),
    ...mapGetters('themeModule', ['activeActivityWithTheme', 'currentPresentationTheme',
      'canViewThemeGallery', 'themeBaseColour', 'activeActivityBaseColour']),
    slide() {
      return this.activeActivityWithTheme
    },
    ...mapGetters('themeModule', ['isTextColourLight', 'contrastBackgroundColour']),
    privateMode() {
      return this.presentation && this.presentation.privateMode
    },
    isReactionsVisible() {
      return !this.presentation.privateMode && this.slide.id
    },
    isHome() {
      return this.$route.name === 'home'
    },
    isSlideTab() {
      return this.activeTabView === 'slide'
    },
    backgroundStyle() {
      if (this.activeTabView === 'slide') {
        return {
          opacity: this.slide.type === 'freestyle' ? this.slide.visibility / 250 : this.slide.visibility / 100,
          'background-image': `url(${this.slide.backgroundImage.large})`,
        }
      }
      return {
        opacity: 1,
        background: (this.$store.state.isDarkTheme || !this.isTextColourLight) ? '#fff' : '#464646',
      }
    },
    canShowSlideProgressBar() {
      return this.presentation
        && this.presentation.id
        && !this.presentation.audiencePacing
    },
    isAudiencePaced() {
      return this.presentation
        && this.presentation.id
        && this.presentation.audiencePacing
    },
  },
  watch: {
    activeTabView: {
      handler(value) {
        this.countRender = 0

        document.body.classList.remove(...TABS)
        document.body.classList.add(value)
        window.scrollTo(0, 0)
      },
      immediate: true,
    },
    '$route.query': {
      handler(query) {
        const attendedRoute = 'attended-presentation-detail'

        if (query[`${attendedRoute}`]) {
          document.body.classList.add('attended-presentation')
        } else {
          document.body.classList.remove('attended-presentation')
        }

        window.scrollTo(0, 0)
      },
      immediate: true,
    },
    slide: function handleSlideChanged(newData) {
      if (newData.id) {
        if (
          this.$store.state.isDarkTheme

        && !this.slide.backgroundImage
        && newData.baseColour
        && newData.baseColour.toLowerCase() === baseBgColor) {
          document.body.style.backgroundColor = 'rgb(34, 34, 34)'
          document.body.style.color = 'white'
          this.countRender++
        } else if (!this.slide.backgroundImage && newData.baseColour && newData.baseColour.toLowerCase() === '#222222') {
          document.body.style.backgroundColor = '#464646'
          document.body.style.color = newData.textColour
        } else {
          document.body.style.backgroundColor = newData.baseColour
          document.body.style.color = newData.textColour
        }

        const qnaSessionBox = 'qna-session-box'

        if (document.getElementById(qnaSessionBox)) {
          document.getElementById(qnaSessionBox).style.backgroundColor = this.contrastBackgroundColour
          document.getElementById(qnaSessionBox).style.color = newData.textColour
        }
      }
    },
    '$store.state.disableScrollBody': function handleDisableScrollBodyChanged(boolean) {
      if (boolean) {
        document.body.classList.add('no-scroll')
      } else {
        document.body.classList.remove('no-scroll')
      }
    },
    $route(refreshPage) {
      if (window.ga) {
        // do not track pageview when switching next/black slide (page with hash)
        // it will be manually sent in Presenter.view
        if (!refreshPage.hash) {
          window.ga('send', 'pageview', refreshPage.fullPath)
        }
      }
    },
    privateMode: {
      handler(value) {
        if (value) {
          document.body.classList.add('is-private-mode')
        } else {
          document.body.classList.remove('is-private-mode')
        }
      },
    },
    isAudiencePaced: {
      handler(value) {
        if (value) {
          document.body.classList.add('is-audience-paced')
        } else {
          document.body.classList.remove('is-audience-paced')
        }
      },
    },
  },

  async created() {
    await this.initializeFeatureToggles()

    Util.migrateTokenCookie()
    const isDarkTheme = !!Util.getParamValue('dark-theme', window.location.href)
    this.$store.state.isDarkTheme = isDarkTheme

    if (isDarkTheme && !!this.slide.backgroundImage) {
      document.body.style.backgroundColor = 'rgb(34, 34, 34)'
      document.body.style.color = 'white'
    }
    this.initDefaultLanguage()
  },
  async mounted() {
    zoomUtils.checkIsZoomApp()
    this.setUserFirstPage()
    Api.initResources()
    await this.getCurrentUser()
    this.$store.commit('setUserLoaded', true)

    this.initCurrentTokenValue()

    // setup vue resource in order to make AJAX requests
    this.checkShowToastedLogin()

    try {
      // FIXME: Consider removing this as there should be any reason why we need
      // a specific class for safari. This was added by Loi
      // to fix an issue with safari but we lost track of the original issue.
      // @huongnguyen2152 15.05.2020
      // eslint-disable-next-line
      let isSafari = /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));
      const ua = window.navigator.userAgent
      const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i)
      const webkit = !!ua.match(/WebKit/i)
      const iOSSafari = iOS && webkit && !ua.match(/CriOS/i)
      if (isSafari || iOSSafari) {
        document.body.classList.add('safari-env')
      }
    } catch (error) {
      Sentry.captureException(error)
    }
    if (Util.checkWhiteLabelDomain()) {
      Util.mixpanelTrack(
        'White Label',
        'Open White-Labelled Audience app',
        `Open White-Labelled Audience app at ${window.location.host}`,
      )
    }

    window.addEventListener('offline', () => {
      this.$store.state.socketDisconnect = true
      Util.mixpanelTrack(
        'Network Status',
        'Offline',
        this.$store.state.presentation.id,
        { nonInteraction: true },
      )
    })

    window.addEventListener('online', () => {
      this.$store.state.socketDisconnect = false
      Util.mixpanelTrack(
        'Network Status',
        'Online',
        this.$store.state.presentation.id,
        { nonInteraction: true },
      )
    })

    this.onVisibilityChange()

    this.clearExpiredLocalStorage()
  },
  beforeDestroy() {
    this.offVisibilityChange()
  },
  methods: {
    ...mapActions('featureToggles', ['initializeFeatureToggles']),
    ...mapActions(['setCurrentUserAction', 'setUserLoaded', 'setIsLoadingCurrentUserAction']),
    setFlagUserUpdatedGA4(user) {
      if (!user || user.isGuest) {
        return
      }
      const keyFlag = `updated-audience-ga4-${user.id}`
      const updatedGA4 = localStorageExpiry.getWithExpiry(keyFlag)
      if (!updatedGA4) {
        Util.ga4Set('user_properties', {
          first_page: user.firstPage,
          referral_user_id: user.referralUserId,
          sign_up_source: user.source,
          use_ahaslides_for: user.personalInformation ? user.personalInformation.useAhaSlidesFor : null,
          updated_at: user.updatedAt,
        })
        localStorageExpiry.setWithExpiry(keyFlag, true, 30 * 24 * 60 * 60 * 1000)
      }

      const keyFlagUpdateSource = `updated-ga4-user-source-${user.id}`
      const updatedUserSourceGA4 = localStorageExpiry.getWithExpiry(keyFlagUpdateSource)
      if (!updatedUserSourceGA4) {
        Util.ga4Set('user_properties', {
          sign_up_source: user.source,
        })
        localStorageExpiry.setWithExpiry(keyFlagUpdateSource, true, 3650 * 24 * 60 * 60 * 1000)
      }
    },
    async getCurrentUser() {
      const token = cookie.getValue('ahaToken')

      if (!token) {
        return
      }

      this.setIsLoadingCurrentUserAction(true)
      Vue.http.headers.common.Authorization = `Bearer ${token}`
      try {
        const currentUser = await UserApi.getUser()
        if (currentUser) {
          await this.setCurrentUserAction(currentUser)
          this.setFlagUserUpdatedGA4(currentUser)
          return
        }
      } catch (e) {
        Sentry.captureException(e)
      } finally {
        this.setIsLoadingCurrentUserAction(false)
      }
    },
    checkShowToastedLogin() {
      if (this.currentUser && this.currentUser.id) {
        const showToasted = window.localStorage.getItem(SHOW_TOASTED_LOGIN)
        if (showToasted) {
          Util.showToastSuccess(`You’ve just logged in as ${this.currentUser.email}`)
          window.localStorage.removeItem(SHOW_TOASTED_LOGIN)
        }
      }
    },
    setUserFirstPage() {
      const getCookie = function (name) {
        const value = `; ${document.cookie}`
        const parts = value.split(`; ${name}=`)
        if (parts.length === 2) {
          return parts.pop().split(';').shift()
        }
        return null
      }
      const setCookie = function (name, value) {
        const cookieName = name
        const cookieValue = value
        const myDate = new Date()
        myDate.setMonth(myDate.getMonth() + 12)
        document.cookie = `${cookieName}=${cookieValue};expires=${myDate};domain=.ahaslides.com;path=/`
        document.cookie = `${cookieName}=${cookieValue};expires=${myDate};domain=.ahaslide.com;path=/`
        document.cookie = `${cookieName}=${cookieValue};expires=${myDate};domain=localhost;path=/`
      }
      const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      })
      const isFromLogout = params.from
      const firstPage = getCookie('ahaFirstPage')
      const ahaToken = getCookie('ahaToken')
      const isGuest = getCookie('aha-guest-user')
      if (firstPage || (ahaToken && !isGuest) || isFromLogout) {
        return
      }
      setCookie('ahaFirstPage', window.location.href)
    },
    _triggerReloadToSyncLoginState() {
      if (document.hidden) return

      // browser tab is active
      const cookieToken = cookie.getValue('ahaToken')
      const hasUser = this.currentUser && this.currentUser.id

      // if user is logged out in another tab
      if (!cookieToken && hasUser) {
        window.location.reload()
      }

      // if user is logged in another tab
      if (cookieToken && !hasUser) {
        window.location.reload()
      }

      // if user is logged in another tab with different account
      if (cookieToken && this.currentTokenValue && this.currentTokenValue !== cookieToken) {
        window.location.reload()
      }
    },
    onVisibilityChange() {
      document.addEventListener('visibilitychange', this._triggerReloadToSyncLoginState)
    },
    offVisibilityChange() {
      document.removeEventListener('visibilitychange', this._triggerReloadToSyncLoginState)
    },
    initCurrentTokenValue() {
      const ahaToken = cookie.getValue('ahaToken')

      if (ahaToken) {
        this.$store.commit('setCurrentTokenValue', cookie.getValue('ahaToken'))
      }
    },
    clearExpiredLocalStorage() {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key.includes('chat-messages-')) {
          try {
            const data = JSON.parse(localStorage.getItem(key))
            if (data.expiry) {
              localStorageExpiry.getWithExpiry(key)
            } else {
              localStorage.removeItem(key)
            }
          } catch (error) {
            Sentry.captureException(error)
          }
        }
      }
    },

    async initDefaultLanguage() {
      try {
        const defaultLocale = 'en'
        const data = await getPresentingLanguageFromCdn()
        if (data) {
          this.$i18n.setLocaleMessage(defaultLocale, data)
        }
      } catch (error) {
        // do nothing
      }
    },
  },
}
</script>

<style lang="scss">
// global layout
body {
  --h-universal-main-total-pb: 0px;
  --h-universal-main-default-pb: 24px;
  --h-audience-paced: 60px;
  --h-quiz-result: 90px;

  &.slide {
    --h-universal-main-total-pb: var(--h-universal-main-default-pb);
  }

  &.slide.is-audience-paced:not(.quiz-result-open) {
    --h-universal-main-total-pb: calc(
      var(--h-universal-main-default-pb) + var(--h-audience-paced)
    );
  }

  &.slide.quiz-result-open:not(.is-audience-paced) {
    --h-universal-main-total-pb: calc(
      var(--h-universal-main-default-pb) + var(--h-quiz-result)
    );
  }

  &.slide.quiz-result-open.is-audience-paced {
    --h-universal-main-total-pb: calc(
      var(--h-universal-main-default-pb) + var(--h-quiz-result) + var(--h-audience-paced)
    );
  }

  .grid-universal {
    display: grid;
    min-height: 100%;
    position: relative;
    z-index: 20;
    grid-template-areas:
      "grid-universal-header"
      "grid-universal-main"
      "grid-universal-footer";
    grid-template-rows: auto 1fr auto;

    &-main {
      padding-bottom: var(--h-universal-main-total-pb);
    }
  }
}
</style>

<style lang="scss">
// Temporary solution to fix the *** dark mode
body.lookback,
body.attended-presentation,
body.profile {
  background: #F8FBFC !important;
  color: inherit !important;

  #slide-image-bg {
    display: none !important;
  }
}
</style>

<style lang="scss">
.slide-type-image-bg {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  &.custom-invisible {
    visibility: hidden;
  }
}

.emoji-mart-preview {
  display: none !important;
}
</style>
